import { useEffect, useRef } from 'react';

import useInterval from '@use-it/interval';

import { DEFAULT_IDLE_TIME, INTERVAL_TIME } from '../../common/constants';
import { type Product, type ProductData } from '../../common/types';
import { useRemoteActions } from '../../services/remote-actions-store';
import { useBrowserInteractionTime } from '../use-browser-interaction-time';
import {
	useRemoteActionsService,
	useRemoteActionsServiceLegacy,
} from '../use-remote-actions-service';

export const useRemoteActionsOnIntervalLegacy = ({
	endpoint,
	product,
	productData,
	pollingInterval,
	idleTime,
}: {
	endpoint: string;
	product: Product;
	productData: ProductData;
	pollingInterval?: number | undefined;
	idleTime?: number | undefined;
}) => {
	const { setCallRemoteActionsService, callRemoteActionsService, deactivated, remoteActionsData } =
		useRemoteActionsServiceLegacy(endpoint, product);
	const [{ isLoaded }, { processRemoteActions }] = useRemoteActions((state) => ({
		isLoaded: state.isLoaded,
	}));
	const { active, tabActive } = useBrowserInteractionTime(idleTime || DEFAULT_IDLE_TIME);

	useEffect(() => {
		if (!isLoaded || remoteActionsData === null) {
			return;
		}

		processRemoteActions(productData, remoteActionsData);
	}, [processRemoteActions, isLoaded, product, productData, remoteActionsData]);

	useInterval(() => {
		if (callRemoteActionsService) {
			return;
		}

		// If the user is not active, do nothing.
		if (!active || !tabActive) {
			return;
		}

		setCallRemoteActionsService(true);
	}, pollingInterval || INTERVAL_TIME);

	return {
		deactivated,
	};
};

export const useRemoteActionsOnInterval = ({
	endpoint,
	product,
	productData,
	pollingInterval,
	idleTime,
}: {
	endpoint: string;
	product: Product;
	productData: ProductData;
	pollingInterval?: number | undefined;
	idleTime?: number | undefined;
}) => {
	const currentTime = Date.now();
	const interval = pollingInterval || INTERVAL_TIME;
	const { active, tabActive } = useBrowserInteractionTime(idleTime || DEFAULT_IDLE_TIME);

	const previousActiveState = useRef({
		active,
		tabActive,
		timestamp: currentTime,
	});

	const {
		setCallRemoteActionsService,
		callRemoteActionsService,
		deactivated,
		setDeactivated,
		remoteActionsData,
	} = useRemoteActionsService(endpoint, product);

	const [{ isLoaded }, { processRemoteActions }] = useRemoteActions((state) => ({
		isLoaded: state.isLoaded,
	}));

	useEffect(() => {
		if (!isLoaded || remoteActionsData === null) {
			return;
		}

		processRemoteActions(productData, remoteActionsData);
	}, [processRemoteActions, isLoaded, product, productData, remoteActionsData]);

	useInterval(() => {
		if (callRemoteActionsService) {
			return;
		}

		// If the user is not active, do nothing.
		if (!active || !tabActive) {
			return;
		}

		previousActiveState.current.timestamp = currentTime;
		setCallRemoteActionsService(true);
	}, interval);

	if (
		active &&
		tabActive &&
		(previousActiveState.current.active !== active ||
			previousActiveState.current.tabActive !== tabActive) &&
		currentTime - previousActiveState.current.timestamp > interval
	) {
		if (deactivated) {
			setDeactivated(false);
		}
		previousActiveState.current.timestamp = currentTime;
		setCallRemoteActionsService(true);
	}

	previousActiveState.current.active = active;
	previousActiveState.current.tabActive = tabActive;

	return {
		deactivated,
	};
};
